import React, { useEffect, useMemo, useState } from 'react';
import { fetchPublicSiteConfiguration, marketingTagsLoadedEventName } from '../../services/marketing.service';
import type { SiteConfiguration } from '../../types/site.types';
import { isSafeForMarketingTags } from '../../utils/analytics-url';
import styles from './MarketingTags.module.scss';

const consentStorageKey = 'combattv_marketing_consent';
const consentGranted = 'granted';
const consentDenied = 'denied';

const readConsent = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage.getItem(consentStorageKey);
    } catch {
        return null;
    }
};

const writeConsent = (value: string) => {
    try {
        window.localStorage.setItem(consentStorageKey, value);
    } catch {
        // Marketing consent is remembered when storage is available; the banner still works for this page load.
    }
};

const appendScriptOnce = (id: string, src: string, inlineBeforeAppend?: (script: HTMLScriptElement) => void) => {
    if (document.getElementById(id)) {
        return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    inlineBeforeAppend?.(script);
    document.head.appendChild(script);
};

const loadGoogleTagManager = (containerId: string) => {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    appendScriptOnce('combattv-gtm', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);
};

const loadGoogleTag = (config: SiteConfiguration) => {
    const primaryId = config.GoogleAnalyticsMeasurementId || config.GoogleAdsConversionId;
    if (!primaryId) {
        return;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = window.gtag ?? function gtagFallback(...args: unknown[]) {
        window.dataLayer?.push(args);
    };

    appendScriptOnce('combattv-gtag', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`);
    window.gtag('js', new Date());
    if (config.GoogleAnalyticsMeasurementId) {
        window.gtag('config', config.GoogleAnalyticsMeasurementId);
    }
    if (config.GoogleAdsConversionId) {
        window.gtag('config', config.GoogleAdsConversionId);
    }
};

const loadMetaPixel = (pixelId: string) => {
    if (!window.fbq) {
        type MetaPixelQueue = ((...args: unknown[]) => void) & { queue: unknown[][]; loaded: boolean; version: string };
        const fbq = function fbqFallback(...args: unknown[]) {
            fbq.queue.push(args);
        } as MetaPixelQueue;
        fbq.queue = [];
        fbq.loaded = true;
        fbq.version = '2.0';
        window.fbq = fbq;
    }

    appendScriptOnce('combattv-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq?.('init', pixelId);
    window.fbq?.('track', 'PageView');
};

const loadMarketingTags = (config: SiteConfiguration) => {
    window.__combattvMarketingConfig = config;
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
        event: 'combattv_marketing_config_loaded',
        googleTagManagerEnabled: Boolean(config.EnableGoogleTagManager),
        metaPixelEnabled: Boolean(config.EnableMetaPixel)
    });

    if (config.EnableGoogleTagManager && config.GoogleTagManagerContainerId) {
        loadGoogleTagManager(config.GoogleTagManagerContainerId);
    } else {
        loadGoogleTag(config);
    }

    if (config.EnableMetaPixel && config.MetaPixelId) {
        loadMetaPixel(config.MetaPixelId);
    }
};

/**
 * Loads consent-aware Google and Meta browser tags from public site configuration.
 */
const MarketingTags = () => {
    const [config, setConfig] = useState<SiteConfiguration | null>(null);
    const [consent, setConsent] = useState<string | null>(() => readConsent());
    const [initialUrlIsSafe] = useState(() => (
        typeof window !== 'undefined' && isSafeForMarketingTags(window.location.href)
    ));
    const canLoadTags = useMemo(() => {
        if (!initialUrlIsSafe || !config?.EnableMarketingTags) {
            return false;
        }

        return !config.RequireMarketingConsent || consent === consentGranted;
    }, [config, consent, initialUrlIsSafe]);

    useEffect(() => {
        let isMounted = true;
        fetchPublicSiteConfiguration()
            .then((siteConfig) => {
                if (isMounted) {
                    setConfig(siteConfig);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setConfig(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!config) {
            return;
        }

        window.__combattvMarketingConfig = config;
        if (canLoadTags) {
            loadMarketingTags(config);
            window.dispatchEvent(new Event(marketingTagsLoadedEventName));
        }
    }, [canLoadTags, config]);

    if (!initialUrlIsSafe || !config?.EnableMarketingTags || !config.RequireMarketingConsent || consent) {
        return null;
    }

    return (
        <aside className={styles.consentBanner} aria-label="Marketing preferences">
            <p className={styles.consentTitle}>Marketing preferences</p>
            <p className={styles.consentCopy}>
                Allow Google and Meta tags so we can measure ads and improve what we promote.
            </p>
            <div className={styles.consentActions}>
                <button
                    type="button"
                    className={styles.declineButton}
                    onClick={() => {
                        writeConsent(consentDenied);
                        setConsent(consentDenied);
                    }}
                >
                    Decline
                </button>
                <button
                    type="button"
                    className={styles.acceptButton}
                    onClick={() => {
                        writeConsent(consentGranted);
                        setConsent(consentGranted);
                    }}
                >
                    Allow
                </button>
            </div>
        </aside>
    );
};

export default MarketingTags;
